import { Component, ChangeDetectionStrategy } from '@angular/core'
import { AbstractControl, FormControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { LoadingService } from '@app/core'
import { NotificationService } from '@app/core/services/notification.service'
import type { ResetPassword } from '@app/session/models/password.model'
import { LostPasswordService } from '@app/session/services/lost-password.service'
import { RouterQuery } from '@datorama/akita-ng-router-store'
import { Observable } from 'rxjs'

type PasswordFormData = {
  password: FormControl<string>
  confirmPassword: FormControl<string>
}

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResetPasswordComponent {
  form: FormGroup<PasswordFormData>

  isLoading$: Observable<boolean>

  constructor(
    private lostPasswordService: LostPasswordService,
    private router: Router,
    private notificationService: NotificationService,
    private routerQuery: RouterQuery,
    formBuilder: FormBuilder,
    loadingService: LoadingService,
  ) {
    this.isLoading$ = loadingService.isLoading$

    this.form = formBuilder.group(
      {
        password: ['', Validators.minLength(6)],
        confirmPassword: '',
      },
      { validators: this.passwordMatchValidator },
    )
  }

  get password(): AbstractControl {
    return this.form.get('password')
  }

  get confirmPassword(): AbstractControl {
    return this.form.get('confirmPassword')
  }

  onSubmit(): void {
    const formData = this.form.value
    const data: ResetPassword = {
      Email: this.routerQuery.getQueryParams('username'),
      Password: formData.password,
      ConfirmPassword: formData.confirmPassword,
      Token: this.routerQuery.getQueryParams('token'),
    }

    this.lostPasswordService.resetPassword(data).subscribe({
      next: () => {
        this.notificationService.success('UI.ResetPassword.Message.ResetSucceeded', null, { duration: 15000 })
        this.router.navigateByUrl('/login')
      },
      error: () => this.notificationService.error('UI.ResetPassword.Message.ResetFailed'),
    })
  }

  onPasswordInput(): void {
    const { ...errors } = this.confirmPassword.errors || {}
    if (this.form.hasError('passwordMismatch')) {
      this.confirmPassword.setErrors({ ...errors, passwordMismatch: true })
    } else {
      this.confirmPassword.setErrors({ ...errors })
      this.confirmPassword.updateValueAndValidity()
    }
  }

  passwordMatchValidator: ValidatorFn = (): ValidationErrors | null => {
    if (!this.form) {
      return null
    }
    const { password, confirmPassword } = this.form.value
    if (password === confirmPassword) {
      return null
    }
    return { passwordMismatch: true }
  }
}
